import React, { Suspense, useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Canvas } from '@react-three/fiber'
import { Stars } from '@react-three/drei'
import { CinematicBackground } from '../components/canvas/CinematicBackground'
import FloatingSidebar from '../components/FloatingSidebar'
import { Zap, Cloud, Cpu, Image as ImageIcon, Layers, Infinity, CreditCard, ShieldCheck, MessageCircle, DollarSign, Calculator, Clock, ArrowRight as ArrowRightIcon } from 'lucide-react'
import ReferralSection from '../components/ReferralSection'
import { useAuth } from '../contexts/SupabaseAuthContext'
import { getUserTokenBalance } from '../services/unifiedTokenService'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

export default function TokensPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [balance, setBalance] = useState<number | null>(null)
  const [customAmount, setCustomAmount] = useState<number>(1000)

  useEffect(() => {
    if (user) {
      getUserTokenBalance(user.id).then(setBalance)
    }
  }, [user])

  const handleCustomRecharge = () => {
    // Implementar lógica de recarga personalizada
    const amount = customAmount
    const price = amount * 0.10
    toast.info(`Recarga personalizada de ${amount} tokens iniciada`)
    navigate('/checkout/pix', { state: { amount: amount, price: price } })
  }

  const handlePurchase = (totalTokens: number, price: number, bonusIncluded: number) => {
    navigate('/checkout/pix', { 
        state: { 
            amount: totalTokens, 
            price: price,
            description: `${totalTokens} Tokens ${bonusIncluded > 0 ? `(inclui ${bonusIncluded} bônus)` : ''}`
        } 
    })
  }

  const packages = [
    { tokens: 100, price: 10, bonus: 0 },
    { tokens: 500, price: 50, bonus: 50 },
    { tokens: 1000, price: 90, bonus: 150, recommended: true },
    { tokens: 5000, price: 400, bonus: 1000 },
  ]

  return (
    <div className="relative w-full min-h-screen bg-black text-white selection:bg-cyan-500/30 overflow-x-hidden">
      <FloatingSidebar />
      
      {/* BACKGROUND 3D (Fixo) */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <Canvas gl={{ antialias: false }} dpr={[1, 1.5]}>
          <Suspense fallback={null}>
            <CinematicBackground />
            <Stars radius={100} depth={50} count={3000} factor={4} saturation={0} fade speed={1} />
          </Suspense>
        </Canvas>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/80 to-black z-10" />
      </div>

      <div className="relative z-10 w-full pt-20 pb-32 px-4 md:px-8 max-w-7xl mx-auto">
        
        {/* HERO SECTION */}
        <section className="flex flex-col md:flex-row items-center justify-between gap-12 mb-32">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="flex-1 space-y-6"
          >
            <div className="inline-block px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-400 text-sm font-bold mb-2">
              <Zap className="inline-block w-4 h-4 mr-2" />
              Central de Tokens
            </div>
            <h1 className="text-5xl md:text-7xl font-black tracking-tighter leading-tight">
              RECARREGUE <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 animate-gradient-x">
                SEU PODER
              </span>
            </h1>
            <p className="text-xl text-gray-400 max-w-xl">
              Tokens são o combustível das ferramentas Overpixel. Quanto mais você tem, mais cria.
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="w-full md:w-auto min-w-[350px]"
          >
            <div className="bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur-xl relative overflow-hidden group hover:border-cyan-500/30 transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-purple-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="relative z-10">
                <p className="text-sm text-gray-400 font-bold tracking-widest uppercase mb-2">Saldo disponível</p>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-6xl font-black text-white">{balance !== null ? balance : '399'}</span>
                  <span className="text-xl text-cyan-400 font-bold">tokens</span>
                </div>
                <p className="text-sm text-gray-500 mb-8">≈ R$ {(balance !== null ? balance * 0.10 : 39.90).toFixed(2)} em poder criativo</p>

                <div className="space-y-4 pt-6 border-t border-white/10">
                  <p className="text-xs text-gray-400 font-bold uppercase">Com esse saldo você consegue:</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center text-purple-400">
                        <Cpu size={16} />
                      </div>
                      <span className="text-sm font-medium">Overbuilder</span>
                    </div>
                    <span className="text-white font-bold">{Math.floor((balance || 399) / 12)} gerações</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-pink-500/20 flex items-center justify-center text-pink-400">
                        <ImageIcon size={16} />
                      </div>
                      <span className="text-sm font-medium">DTF Factory</span>
                    </div>
                    <span className="text-white font-bold">{Math.floor((balance || 399) / 20)} artes</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* CUSTO POR FERRAMENTA */}
        <section className="mb-32">
          <div className="text-center mb-16">
            <h2 className="text-sm font-bold text-cyan-400 tracking-widest uppercase mb-3">CUSTO POR FERRAMENTA</h2>
            <h3 className="text-3xl md:text-5xl font-bold">Tokens desbloqueiam o poder da IA</h3>
            <p className="text-gray-400 mt-4">Veja quanto custa usar cada ferramenta</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                icon: Cpu, 
                color: "text-purple-400", 
                bg: "bg-purple-500/10", 
                title: "Overbuilder", 
                desc: "Gerador de imagens com IA", 
                cost: "12", 
                unit: "por geração" 
              },
              { 
                icon: ImageIcon, 
                color: "text-pink-400", 
                bg: "bg-pink-500/10", 
                title: "DTF Factory", 
                desc: "Criação de artes para impressão DTF", 
                cost: "20", 
                unit: "por arte" 
              },
              { 
                icon: Cloud, 
                color: "text-blue-400", 
                bg: "bg-blue-500/10", 
                title: "Melhorador Cloud", 
                desc: "Melhora resolução de imagens com IA", 
                cost: "5", 
                unit: "por imagem" 
              },
              { 
                icon: Infinity, 
                color: "text-green-400", 
                bg: "bg-green-500/10", 
                title: "Spot Cloud", 
                desc: "Spot White automático — sem Photoshop", 
                cost: "∞", 
                unit: "ASSINATURA" 
              },
            ].map((tool, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors"
              >
                <div className={`w-12 h-12 rounded-xl ${tool.bg} flex items-center justify-center ${tool.color} mb-4`}>
                  <tool.icon size={24} />
                </div>
                <h4 className="text-xl font-bold mb-1">{tool.title}</h4>
                <p className="text-sm text-gray-400 mb-6 min-h-[40px]">{tool.desc}</p>
                <div className="flex items-baseline gap-1">
                  <span className={`text-3xl font-black ${tool.color}`}>{tool.cost}</span>
                  <span className="text-xs text-gray-500 uppercase font-bold">{tool.unit}</span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Assinatura Highlight */}
          <div className="mt-6 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20 rounded-2xl p-6 flex items-center justify-between flex-col md:flex-row gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center text-yellow-400 shrink-0">
                <Zap size={24} />
              </div>
              <div>
                <h4 className="text-lg font-bold text-white">Montador Fast</h4>
                <p className="text-gray-400">Monta artes e estampas rapidinho</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Infinity className="text-yellow-400" size={32} />
              <span className="text-sm font-bold text-yellow-400 uppercase tracking-wider">ASSINATURA</span>
            </div>
          </div>
        </section>

        {/* REFERRAL SYSTEM */}
        <ReferralSection />

        {/* RECARGA */}
        <section className="mb-32" id="recharge">
          <div className="text-center mb-16">
            <h2 className="text-sm font-bold text-green-400 tracking-widest uppercase mb-3">RECARREGUE SEUS TOKENS</h2>
            <h3 className="text-3xl md:text-5xl font-bold">Escolha seu pacote</h3>
            <p className="text-gray-400 mt-4">Quanto maior, mais barato por token + bônus exclusivos</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Pacote Básico */}
            <div className="bg-black/40 border border-white/10 rounded-3xl p-8 relative hover:border-white/30 transition-all flex flex-col">
              <h4 className="text-xl font-bold text-gray-400 mb-2">Básico</h4>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-black text-white">100</span>
                <span className="text-sm font-bold text-gray-500">tokens</span>
              </div>
              <ul className="space-y-3 mb-8 text-sm text-gray-400 flex-1">
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-white/20" /> ~8 gerações Overbuilder</li>
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-white/20" /> ~5 artes DTF Factory</li>
              </ul>
              <div className="mt-auto">
                <div className="text-center mb-2 text-sm text-gray-500 font-medium opacity-0 select-none">Sem desconto</div>
                <button 
                  onClick={() => handlePurchase(100, 10, 0)}
                  className="w-full py-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 font-bold transition-all group-hover:border-white/30"
                >
                  Comprar por R$ 10,00
                </button>
              </div>
            </div>

            {/* Pacote Popular */}
            <div className="bg-gradient-to-b from-cyan-900/20 to-black border border-cyan-500/50 rounded-3xl p-8 relative transform md:-translate-y-4 shadow-2xl shadow-cyan-900/20 flex flex-col">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-cyan-500 text-black text-xs font-black uppercase tracking-widest py-1 px-3 rounded-full shadow-[0_0_15px_rgba(6,182,212,0.6)]">
                Mais Popular
              </div>
              <div className="absolute top-4 right-4 bg-green-500/20 text-green-400 border border-green-500/30 text-[10px] font-black uppercase tracking-wider py-1 px-2 rounded-lg">
                10% OFF
              </div>
              <h4 className="text-xl font-bold text-cyan-400 mb-2">Pro</h4>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-5xl font-black text-white">900</span>
                <span className="text-sm font-bold text-cyan-500">tokens</span>
              </div>
              <p className="text-xs text-green-400 font-bold mb-6 flex items-center gap-1">
                <Zap size={12} fill="currentColor" />
                + 100 tokens de bônus
              </p>
              
              <ul className="space-y-3 mb-8 text-sm text-gray-300 flex-1">
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-cyan-500" /> 83 gerações Overbuilder</li>
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-cyan-500" /> 50 artes DTF Factory</li>
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-cyan-500" /> 200 melhorias de imagem</li>
              </ul>
              <div className="mt-auto">
                <div className="text-center mb-2 text-sm text-gray-400 font-medium">
                  De <span className="line-through text-gray-600">R$ 100,00</span> por
                </div>
                <button 
                  onClick={() => handlePurchase(1000, 90, 100)}
                  className="w-full py-4 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-black font-black text-lg transition-all shadow-[0_0_20px_rgba(6,182,212,0.3)] hover:shadow-[0_0_30px_rgba(6,182,212,0.5)] active:scale-95 flex items-center justify-center gap-2"
                >
                  Comprar por R$ 90,00
                  <ArrowRightIcon size={20} className="stroke-[3px]" />
                </button>
              </div>
            </div>

            {/* Pacote Avançado */}
            <div className="bg-black/40 border border-white/10 rounded-3xl p-8 relative hover:border-white/30 transition-all flex flex-col">
              <div className="absolute top-4 right-4 bg-purple-500/20 text-purple-400 border border-purple-500/30 text-[10px] font-black uppercase tracking-wider py-1 px-2 rounded-lg">
                20% OFF
              </div>
              <h4 className="text-xl font-bold text-purple-400 mb-2">Studio</h4>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-4xl font-black text-white">4000</span>
                <span className="text-sm font-bold text-purple-500">tokens</span>
              </div>
              <p className="text-xs text-green-400 font-bold mb-6 flex items-center gap-1">
                <Zap size={12} fill="currentColor" />
                + 1000 tokens de bônus
              </p>
              
              <ul className="space-y-3 mb-8 text-sm text-gray-400 flex-1">
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-purple-500/50" /> 400+ gerações Overbuilder</li>
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-purple-500/50" /> 250+ artes DTF Factory</li>
              </ul>
              <div className="mt-auto">
                <div className="text-center mb-2 text-sm text-gray-400 font-medium">
                  De <span className="line-through text-gray-600">R$ 500,00</span> por
                </div>
                <button 
                  onClick={() => handlePurchase(5000, 400, 1000)}
                  className="w-full py-4 rounded-xl bg-purple-600/20 border border-purple-500/50 hover:bg-purple-600/30 text-purple-300 hover:text-white font-bold transition-all active:scale-95"
                >
                  Comprar por R$ 400,00
                </button>
              </div>
            </div>
          </div>

          {/* Valor Personalizado */}
          <div className="mt-12 max-w-2xl mx-auto bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col md:flex-row items-center gap-6">
            <div className="flex-1">
              <h4 className="text-lg font-bold mb-2 flex items-center gap-2">
                <DollarSign className="text-green-400" size={20} />
                Valor Personalizado
              </h4>
              <p className="text-sm text-gray-400">Escolha exatamente quanto você quer gastar.</p>
            </div>
            <div className="flex items-center gap-4 bg-black/50 rounded-xl p-2 border border-white/10">
              <input 
                type="number" 
                value={customAmount}
                onChange={(e) => setCustomAmount(Number(e.target.value))}
                className="bg-transparent text-white font-bold text-right w-24 outline-none"
              />
              <span className="text-sm text-gray-500 font-bold pr-2">tokens</span>
            </div>
            <div className="text-right min-w-[100px]">
              <p className="text-xs text-gray-500 font-bold uppercase">Total</p>
              <p className="text-xl font-bold text-white">R$ {(customAmount * 0.10).toFixed(2)}</p>
            </div>
            <button 
              onClick={handleCustomRecharge}
              className="px-6 py-3 rounded-xl bg-white text-black font-bold hover:bg-gray-200 transition-colors"
            >
              Comprar
            </button>
          </div>

          <div className="flex flex-wrap justify-center gap-8 mt-12 text-sm text-gray-500 font-medium">
            <span className="flex items-center gap-2"><CreditCard size={16} /> Pagamento seguro</span>
            <span className="flex items-center gap-2"><Zap size={16} /> Crédito instantâneo</span>
            <span className="flex items-center gap-2"><MessageCircle size={16} /> Suporte disponível</span>
          </div>
        </section>

        {/* ECONOMIA / CALCULADORA */}
        <section className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl p-8 md:p-12 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-green-500/10 blur-[100px] rounded-full pointer-events-none" />
          
          <div className="relative z-10 flex flex-col md:flex-row gap-12 items-center">
            <div className="flex-1 space-y-6">
              <div className="inline-block px-3 py-1 bg-green-500/10 border border-green-500/20 rounded-full text-green-400 text-sm font-bold mb-2">
                <Calculator className="inline-block w-4 h-4 mr-2" />
                ECONOMIA REAL
              </div>
              <h3 className="text-3xl md:text-4xl font-bold">Por que vale a pena?</h3>
              <p className="text-gray-400 text-lg leading-relaxed">
                Designers cobram em média <strong className="text-white">R$ 80/hora</strong>.
                <br />
                A DTF Factory economiza ~2h por projeto — sem abrir o Photoshop.
                Isso vale <strong className="text-white">R$ 160</strong> de trabalho manual.
                <br />
                Você paga <strong className="text-green-400">R$ 10</strong> (aprox. custo de tokens).
              </p>
            </div>

            <div className="flex-1 grid grid-cols-2 gap-4 w-full">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
                <Clock className="w-8 h-8 text-blue-400 mx-auto mb-4" />
                <h4 className="text-3xl font-black text-white mb-1">2h</h4>
                <p className="text-xs text-gray-400 uppercase font-bold">economizadas por projeto</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
                <DollarSign className="w-8 h-8 text-green-400 mx-auto mb-4" />
                <h4 className="text-3xl font-black text-white mb-1">R$ 160</h4>
                <p className="text-xs text-gray-400 uppercase font-bold">valor do trabalho manual</p>
              </div>
              <div className="col-span-2 bg-gradient-to-r from-green-900/20 to-green-500/20 border border-green-500/30 rounded-2xl p-6 flex items-center justify-between">
                <div className="text-left">
                  <h4 className="text-xl font-bold text-white">1 clique</h4>
                  <p className="text-sm text-green-300">para gerar Spot White</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center text-black">
                  <Zap size={24} fill="currentColor" />
                </div>
              </div>
            </div>
          </div>
        </section>

      </div>
    </div>
  )
}
